import React, { useEffect, useRef } from 'react';
import {
  Box,
  Tab,
  Typography,
  Grid,
  InputAdornment,
  OutlinedInput,
  LinearProgress,
  CircularProgress,
  IconButton,
  Dialog,
  Popover,
  Tooltip
} from '@mui/material';
import { useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { IconSearch, IconArrowUp, IconArrowDown, IconSend } from '@tabler/icons';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import MicIcon from '@mui/icons-material/Mic';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import ReplyIcon from '@mui/icons-material/Reply';
import { EMOJI_FONT_STACK, TEXT_WITH_EMOJI_FONT_STACK } from '../emojiFont';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import { useGetConversationsQuery, useGetMessagesQuery, useSendMessageMutation } from 'services/chat/chatAPIs';
import { useAppUserActiveClientsListingQuery } from 'services/user-management/app-users/appUsersAPIs';
import { useTheme } from '@emotion/react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

// Curated emoji set for the inline picker (no external dependency).
const EMOJIS = [
  '😀','😁','😂','🤣','😊','😍','😘','😎','🤩','🥳','😅','😉','🙂','🙃','😋','😌',
  '🤔','🤨','😐','😴','😭','😤','😡','🥺','😬','🙄','😇','🤗','🤝','🙏','👏','💪',
  '👍','👎','👌','✌️','🤞','👋','🔥','✨','⭐','🎉','💯','✅','❌','⚠️','❤️','🧡',
  '💛','💚','💙','💜','🖤','🏋️','🤸','🏃','🥇','🏆','🎯','💧','🥗','🍎','💤','⏰'
];

function formatRecordTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function AdminChat() {
  const theme = useTheme();
  const chatContainerRef = useRef(null);
  const [value, setValue] = useState('1');
  const [selectedChat, setSelectedChat] = useState(null);
  // const [senderId, setSenderId] = useState('');
  // const [receiverId, setReceiverId] = useState('');
  // const [productId, setProductId] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  // State variables
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [isLoadingPreviousMessages, setIsLoadingPreviousMessages] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [searchChat, setSearchChat] = useState('');
  const [searchMessage, setSearchMessage] = useState('');
  const [chat, setChat] = useState([]);
  const [searchMatches, setSearchMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [searchedChat, setSearchedChat] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [messageText, setMessageText] = useState('');
  const [clientTab, setClientTab] = useState('active'); // 'active' | 'expired'
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  // The message being replied to, or null. Held as the whole message so the composer
  // can show who said what without another fetch.
  const [replyTarget, setReplyTarget] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordRoomRef = useRef(null);
  const MAX_RECORD_SECONDS = 60;

  const reloadThread = () => {
    setFrom(1);
    setPage(1);
    setChat([]);
  };

  // Send a reply into the selected conversation. The backend resolves the
  // receiver to the client and the client sees it as "CalisFix Coach".
  const handleSendMessage = async () => {
    const text = messageText.trim();
    if (!text || !selectedChatId) return;
    // Clear the composer BEFORE awaiting the send. Clearing afterwards left the text the
    // coach had just sent sitting in the box for the whole round trip, so it read as the
    // message not having gone. Put it back if the send actually fails.
    setMessageText('');
    const quoted = replyTarget;
    setReplyTarget(null);
    try {
      await sendMessage({
        chatRoomId: selectedChatId,
        content: text,
        ...(quoted?._id ? { replyTo: quoted._id } : {})
      }).unwrap();
      reloadThread();
    } catch (e) {
      // Put both the text AND the reply target back, so a failed send does not
      // silently turn a reply into a loose message on the retry.
      setMessageText(text);
      setReplyTarget(quoted);
      toast.error(e?.data?.message || e?.message || 'Failed to send message');
    }
  };

  const insertEmoji = (emoji) => {
    setMessageText((prev) => `${prev}${emoji}`);
  };

  const cleanupRecording = () => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordSeconds(0);
  };

  // Record an efficient Opus/WebM voice note (max 1 min) and send as attachment.
  const startRecording = async () => {
    if (!selectedChatId) return;
    recordRoomRef.current = selectedChatId;
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported in this browser.');
      return;
    }
    try {
      // Audio capture tuning for a one-way voice note:
      // - autoGainControl OFF: AGC starts at full gain then attenuates after
      //   ~1-2s (the "loud then quiet" notes), so keep it off for steady volume.
      // - echoCancellation ON: removes room/speaker echo from the recording.
      // - noiseSuppression ON: cuts background hiss and softens the record-button
      //   mouse-click transient.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: false,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      recordStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      // Skip the record-button mouse-click: wait briefly so its acoustic tail
      // decays before capture begins. Bail if the user cancelled meanwhile.
      await new Promise((resolve) => setTimeout(resolve, 180));
      if (mediaRecorderRef.current !== recorder) return;
      recorder.start();
      setIsRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          if (s + 1 >= MAX_RECORD_SECONDS) {
            // Auto-stop and send at the 1-minute cap.
            stopRecordingAndSend();
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error('Microphone permission denied or unavailable.');
      cleanupRecording();
    }
  };

  const cancelRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
    } catch (_) {
      // ignore
    }
    cleanupRecording();
  };

  const stopRecordingAndSend = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    recorder.onstop = async () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const roomId = recordRoomRef.current;
      cleanupRecording();
      if (!blob.size || !roomId) return;
      try {
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('chatRoomId', roomId);
        formData.append('attachment', file);
        await sendMessage(formData).unwrap();
        reloadThread();
      } catch (e) {
        toast.error(e?.data?.message || e?.message || 'Failed to send voice message');
      }
    };
    try {
      recorder.stop();
    } catch (_) {
      cleanupRecording();
    }
  };

  // Stop the mic / timer if the screen unmounts mid-recording.
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (recordStreamRef.current) recordStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Helper function to get file icon based on file type/extension
  const getFileIcon = (fileInput, isOwnMessage = false) => {
    if (!fileInput) return <InsertDriveFileIcon sx={{ color: isOwnMessage ? '#fff' : 'text.secondary' }} />;

    // Check if we're dealing with a File object or a URL string
    const isFileObject = fileInput instanceof File;
    const fileType = isFileObject ? fileInput.type : '';
    const fileName = isFileObject ? fileInput.name : String(fileInput);

    // Check MIME type first (for File objects)
    if (isFileObject) {
      if (fileType.startsWith('image/')) {
        return <AddPhotoAlternateIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (fileType === 'application/pdf') {
        return <PictureAsPdfIcon sx={{ color: isOwnMessage ? '#fff' : 'error.main' }} />;
      } else if (
        fileType === 'application/msword' ||
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return <DescriptionIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (
        fileType === 'application/vnd.ms-excel' ||
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        return <TableChartIcon sx={{ color: isOwnMessage ? '#fff' : 'success.main' }} />;
      } else if (fileType.startsWith('video/')) {
        return <VideoFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      } else if (fileType.startsWith('audio/')) {
        return <AudioFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
      }
    }

    // Fall back to extension checking for URLs or when MIME type doesn't match
    if (fileName.match(/\.(jpeg|jpg|gif|png)$/i)) {
      return <AddPhotoAlternateIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.pdf$/i)) {
      return <PictureAsPdfIcon sx={{ color: isOwnMessage ? '#fff' : 'error.main' }} />;
    } else if (fileName.match(/\.(doc|docx)$/i)) {
      return <DescriptionIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.(xls|xlsx)$/i)) {
      return <TableChartIcon sx={{ color: isOwnMessage ? '#fff' : 'success.main' }} />;
    } else if (fileName.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i)) {
      return <VideoFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else if (fileName.match(/\.(mp3|wav|flac|aac|ogg|wma)$/i)) {
      return <AudioFileIcon sx={{ color: isOwnMessage ? '#fff' : 'accent.text' }} />;
    } else {
      return <InsertDriveFileIcon sx={{ color: isOwnMessage ? '#fff' : 'text.secondary' }} />;
    }
  };

  // Handle tab change
  // const handleChange = (event, newValue) => {
  //   setValue(newValue);
  //   setReceiverId('');
  //   setSenderId('');
  //   setSearchMessage('');
  //   setSelectedChat(null);
  //   setSelectedChatId(null);
  //   setChat([]);
  //   setSearchMatches([]);
  // };

  const handleChatClick = (item) => {
    setSelectedChat(item);
    setSelectedChatId(item?._id);
    // setProductId(item?.productId ? item?.productId : null);
    // setSenderId(item?.sender);
    // setReceiverId(item?.receiver);
    setSearchMessage('');
    setFrom(1);
    setPage(1);
    setChat([]);
    setSearchMatches([]);
  };

  const { data: chatsListing, isLoading } = useGetConversationsQuery(
    {
      from: 1,
      limit: 1500
    },
    // Keep the conversation list + last-message previews fresh without a manual
    // refresh (no realtime layer on the backend).
    { pollingInterval: 10000 }
  );

  // Filter chat list by search term (client-side - backend doesn't support search)
  const filteredChatRooms = React.useMemo(() => {
    const rooms = chatsListing?.chatRooms ?? [];
    if (!searchChat?.trim()) return rooms;
    const term = searchChat.trim().toLowerCase();
    return rooms.filter((item) => {
      const participants = item?.chatRoom?.participants ?? [];
      return participants.some(
        (p) =>
          p?.fullName?.toLowerCase().includes(term) || p?.userName?.toLowerCase().includes(term)
      );
    });
  }, [chatsListing?.chatRooms, searchChat]);

  // Active vs expired grouping — reuse the app's active-clients list (a client is
  // "active" if their latest subscription is still valid). Frontend-only: a
  // conversation goes to Active if its client participant is in that set.
  const { data: activeClientsData } = useAppUserActiveClientsListingQuery({ limit: 2000, from: 0, searchName: '' });
  const activeClientIds = React.useMemo(
    () => new Set((activeClientsData?.users || []).map((u) => String(u._id))),
    [activeClientsData]
  );
  const clientIdOfRoom = (item) => {
    const parts = item?.chatRoom?.participants || [];
    const client = parts.find((p) => Number(p?.userType) === 3) || parts[0];
    return client?._id ? String(client._id) : '';
  };
  // unread-first within each group (stable sort preserves the backend's recency order)
  const byUnread = (arr) => [...arr].sort((a, b) => (b?.unreadCount || 0) - (a?.unreadCount || 0));
  const activeRooms = React.useMemo(
    () => byUnread(filteredChatRooms.filter((r) => activeClientIds.has(clientIdOfRoom(r)))),
    [filteredChatRooms, activeClientIds]
  );
  const expiredRooms = React.useMemo(
    () => byUnread(filteredChatRooms.filter((r) => !activeClientIds.has(clientIdOfRoom(r)))),
    [filteredChatRooms, activeClientIds]
  );
  const visibleRooms = clientTab === 'active' ? activeRooms : expiredRooms;

  const { data: usersChat, isLoading: userChatLoader } = useGetMessagesQuery(
    {
      id: selectedChatId,
      from,
      limit: rowsPerPage
      // searchMessage
    },
    {
      skip: !selectedChatId, // Skip the query if no chat is selected
      // No realtime layer (no WebSocket) on the backend, so an incoming message
      // from the other party invalidates nothing on this client. Poll the open
      // thread every 2s so replies appear quickly without a manual refresh.
      pollingInterval: 2000
    }
  );

  // Append new chat messages to existing chat when new data is fetched
  useEffect(() => {
    if (usersChat && usersChat?.messages) {
      const scrollHeightBeforeUpdate = chatContainerRef.current?.scrollHeight;

      setChat((prevChat) => {
        // Avoid duplicate messages by checking their IDs
        const newChats = usersChat.messages.filter((newMsg) => !prevChat.some((oldMsg) => oldMsg._id === newMsg._id));

        // If loading previous messages, add to beginning, otherwise add to end
        return isLoadingPreviousMessages ? [...newChats, ...prevChat] : [...prevChat, ...newChats];
      });

      // Update total pages
      setTotalPages(usersChat.totalPages || 1);

      // Check if there are more messages to load
      setHasMoreMessages(from < usersChat.totalPages);

      // Maintain scroll position when loading previous messages
      if (isLoadingPreviousMessages && chatContainerRef.current) {
        setTimeout(() => {
          const scrollHeightDifference = chatContainerRef.current.scrollHeight - scrollHeightBeforeUpdate;
          chatContainerRef.current.scrollTop = scrollHeightDifference;
          setIsLoadingMoreMessages(false);
          setIsLoadingPreviousMessages(false);
        }, 100);
      }
    }
  }, [usersChat]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current && !isLoadingPreviousMessages) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chat]);

  // Infinite scrolling - load more when reaching the last element
  useEffect(() => {
    if (chat.length > 0 && chat.length < usersChat?.totalPages) {
      const lastChatIndex = chat.length - 1;

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && usersChat?.totalPages > from + rowsPerPage) {
          setPage((prev) => prev + 1);
          setFrom((prev) => prev + rowsPerPage);
        }
      });

      const lastChatElement = document.getElementById(`chat-${lastChatIndex}`);
      if (lastChatElement) {
        observer.observe(lastChatElement);
      }

      return () => {
        if (lastChatElement) observer.unobserve(lastChatElement);
      };
    }
  }, [chat, usersChat, from, rowsPerPage]);

  // Count search matches and highlight
  useEffect(() => {
    if (searchMessage && chat.length > 0) {
      const matches = chat
        ?.map((item, index) => (item?.content?.toLowerCase().includes(searchMessage?.toLowerCase()) ? index : null))
        .filter((item) => item !== null);

      setSearchMatches(matches);
      setCurrentMatchIndex(0); // Reset to the first match
    } else {
      setSearchMatches([]); // Clear matches if no search term
    }
  }, [searchMessage, chat]);

  // Search logic - Count matches and update searchedChat
  useEffect(() => {
    if (searchMessage && chat.length > 0) {
      const matches = chat
        ?.map((item, index) =>
          item?.attachment ? null : item?.content?.toLowerCase().includes(searchMessage?.toLowerCase()) ? index : null
        )
        ?.filter((item) => item !== null);

      setSearchMatches(matches);
      setSearchedChat(matches?.map((index) => chat[index])); // Store matched chat items
      setCurrentMatchIndex(0); // Reset to the first match
    } else {
      setSearchMatches([]); // Clear matches if no search term
      setSearchedChat([]);
    }
  }, [searchMessage, chat]);
  const getSearchMatches = (messages, searchTerm) => {
    messages
      ?.map((item, index) => (item?.attachment ? null : item?.content?.toLowerCase().includes(searchTerm?.toLowerCase()) ? index : null))
      ?.filter((item) => item !== null);
  };
  // Scroll to the current match
  useEffect(() => {
    if (searchMatches?.length > 0) {
      const currentElement = document.getElementById(`chat-${searchMatches[currentMatchIndex]}`);
      if (currentElement) {
        currentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentMatchIndex, searchMatches]);

  // Navigate between matches
  const handleMatchNavigation = (direction) => {
    setCurrentMatchIndex((prevIndex) => {
      const newIndex = direction === 'up' ? prevIndex - 1 : prevIndex + 1;
      if (newIndex < 0) {
        return searchMatches.length - 1;
      }
      if (newIndex >= searchMatches.length) {
        return 0;
      }
      return newIndex;
    });
  };

  // Function to handle opening the modal
  const handleOpenModal = (url) => {
    setAttachmentUrl(url);
    setOpenModal(true);
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Scroll handler for loading previous messages
  const handleChatScroll = async (e) => {
    const { scrollTop } = e.target;

    // Load more messages when scrolled near the top (within 100px)
    if (scrollTop < 100 && hasMoreMessages && !isLoadingMoreMessages && !userChatLoader) {
      setIsLoadingMoreMessages(true);
      setIsLoadingPreviousMessages(true);
      const nextPage = Math.floor(chat.length / rowsPerPage) + 1;
      if (nextPage <= totalPages) {
        setFrom(nextPage);
      }
    }
  };

  // Add a function to format timestamps with date information when needed
  const formatMessageTime = (timestamp) => {
    const messageDate = dayjs(timestamp);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');

    // If the message is from today, just show the time
    if (messageDate.isSame(today, 'day')) {
      return messageDate.format('h:mm A');
    }
    // If the message is from yesterday, show "Yesterday" and time
    else if (messageDate.isSame(yesterday, 'day')) {
      return `Yesterday, ${messageDate.format('h:mm A')}`;
    }
    // If the message is from this year, show month, day and time
    else if (messageDate.isSame(today, 'year')) {
      return messageDate.format('MMM D, h:mm A');
    }
    // If the message is from a previous year, include the year
    else {
      return messageDate.format('MMM D YYYY, h:mm A');
    }
  };

  // Function to format date for separators
  const formatDateForSeparator = (timestamp) => {
    const messageDate = dayjs(timestamp);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');

    if (messageDate.isSame(today, 'day')) {
      return 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
      return 'Yesterday';
    } else if (messageDate.isSame(today, 'year')) {
      return messageDate.format('MMMM D');
    } else {
      return messageDate.format('MMMM D, YYYY');
    }
  };

  // Function to check if two dates are different days
  const isDifferentDay = (date1, date2) => {
    if (!date1 || !date2) return true;
    return !dayjs(date1).isSame(dayjs(date2), 'day');
  };

  return (
    <>
      <Grid container sx={{ height: `calc(100vh - 128px)` }}>
        <Grid
          item
          lg={3}
          md={3}
          sm={4}
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            padding: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%',
            bgcolor: theme.palette.background.paper
          }}
        >
          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              <OutlinedInput
                sx={{ width: '100%', mb: 2 }}
                id="input-search-conversation"
                value={searchChat}
                onChange={(e) => setSearchChat(e.target.value)}
                placeholder="Search..."
                endAdornment={
                  <InputAdornment position="end">
                    <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
                  </InputAdornment>
                }
              />
              {/* Active / Expired client tabs */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                {['active', 'expired'].map((t) => (
                  <Box
                    key={t}
                    onClick={() => setClientTab(t)}
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      py: 0.75,
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      textTransform: 'capitalize',
                      // A segmented control: only the selected half carries the accent.
                      color: clientTab === t ? '#fff' : 'text.secondary',
                      border: '1px solid',
                      borderColor: clientTab === t ? 'accent.main' : 'transparent',
                      backgroundColor: clientTab === t ? 'accent.main' : 'transparent',
                      transition: 'background-color .15s ease, color .15s ease',
                      '&:hover': {
                        backgroundColor: clientTab === t ? 'accent.main' : 'surface.hover',
                        color: clientTab === t ? '#fff' : 'text.primary'
                      }
                    }}
                  >
                    {t === 'active' ? `Active (${activeRooms.length})` : `Expired (${expiredRooms.length})`}
                  </Box>
                ))}
              </Box>
              {/* Chat List */}
              <Box sx={{ width: '100%', typography: 'body1' }}>
                {visibleRooms?.length > 0 ? (
                  visibleRooms?.map((item, index) => (
                    <Box
                      key={index}
                      id={`chat-${index}`}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      padding={1}
                      borderRadius="8px"
                      sx={{
                        // Selected and hover were both solid #673ab7, so hovering down the
                        // list made every thread look like the open one.
                        backgroundColor: selectedChatId === item?.chatRoom?._id ? 'accent.soft' : 'transparent',
                        boxShadow: selectedChatId === item?.chatRoom?._id ? (t) => `inset 2px 0 0 ${t.palette.accent.text}` : 'none',
                        cursor: 'pointer',
                        color: selectedChatId === item?.chatRoom?._id ? 'accent.text' : 'text.primary',
                        transition: 'background-color .15s ease',
                        '&:hover': {
                          backgroundColor: selectedChatId === item?.chatRoom?._id ? 'accent.soft' : 'surface.hover'
                        }
                      }}
                      onClick={() => handleChatClick(item?.chatRoom)}
                    >
                      {item?.chatRoom?.participants?.map((user, index) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} key={index}>
                          <img
                            src={user?.profileImage || '/default-avatar.png'}
                            alt={user?.fullName}
                            width="35"
                            height="35"
                            style={{
                              objectFit: 'cover',
                              borderRadius: '50%',
                              backgroundColor: theme.palette.grey[400]
                            }}
                          />
                          <Typography fontWeight={600}>
                            {user?.fullName}
                            {/*{index == 0 && '/'}*/}
                          </Typography>
                        </div>
                      ))}
                    </Box>
                  ))
                ) : (
                  <Typography>
                    {searchChat?.trim()
                      ? 'No conversations match your search.'
                      : `No ${clientTab} conversations.`}
                  </Typography>
                )}
                {/*<TabContext value={value}>*/}
                {/*  <TabList onChange={handleChange} aria-label="community tabs">*/}
                {/*    <Tab label="Conversations" value="1" />*/}
                {/*    <Tab label="Product Conversations" value="2" />*/}
                {/*  </TabList>*/}

                {/*  <TabPanel value="1">*/}
                {/*    {chatsListing?.conversations?.length > 0 ? (*/}
                {/*      chatsListing.conversations.map((item, index) => (*/}
                {/*        <Box*/}
                {/*          key={index}*/}
                {/*          id={`chat-${index}`}*/}
                {/*          display="flex"*/}
                {/*          alignItems="center"*/}
                {/*          gap={2}*/}
                {/*          padding={1}*/}
                {/*          borderRadius="8px"*/}
                {/*          sx={{*/}
                {/*            backgroundColor: selectedChatId === item?._id ? '#f0f0f0' : 'transparent',*/}
                {/*            cursor: 'pointer',*/}
                {/*            '&:hover': {*/}
                {/*              backgroundColor: theme.palette.grey[200]*/}
                {/*            }*/}
                {/*          }}*/}
                {/*          onClick={() => handleChatClick(item)}*/}
                {/*        >*/}
                {/*          <img*/}
                {/*            src={item?.senderDetails?.profileImage || '/default-avatar.png'}*/}
                {/*            alt={item?.senderDetails?.fullName}*/}
                {/*            width="40"*/}
                {/*            height="40"*/}
                {/*            style={{*/}
                {/*              objectFit: 'cover',*/}
                {/*              borderRadius: '50%',*/}
                {/*              backgroundColor: theme.palette.grey[400]*/}
                {/*            }}*/}
                {/*          />*/}
                {/*          <Typography fontWeight={600}>*/}
                {/*            {item?.senderDetails?.fullName} / {item?.receiverDetails?.fullName}*/}
                {/*          </Typography>*/}
                {/*        </Box>*/}
                {/*      ))*/}
                {/*    ) : (*/}
                {/*      <Typography>No conversations found.</Typography>*/}
                {/*    )}*/}
                {/*  </TabPanel>*/}

                {/*  <TabPanel value="2">*/}
                {/*    {chatsListing?.itemConversations?.length > 0 ? (*/}
                {/*      chatsListing.itemConversations.map((item, index) => (*/}
                {/*        <Box*/}
                {/*          key={index}*/}
                {/*          display="flex"*/}
                {/*          alignItems="center"*/}
                {/*          gap={2}*/}
                {/*          padding={1}*/}
                {/*          borderRadius="8px"*/}
                {/*          sx={{*/}
                {/*            cursor: 'pointer',*/}
                {/*            backgroundColor: selectedChatId === item._id ? '#f0f0f0' : 'transparent',*/}
                {/*            '&:hover': {*/}
                {/*              backgroundColor: theme.palette.grey[200]*/}
                {/*            }*/}
                {/*          }}*/}
                {/*          onClick={() => handleChatClick(item)}*/}
                {/*        >*/}
                {/*          <img*/}
                {/*            src={item?.senderDetails?.profileImage || '/default-avatar.png'}*/}
                {/*            alt={item?.senderDetails?.fullName}*/}
                {/*            width="40"*/}
                {/*            height="40"*/}
                {/*            style={{*/}
                {/*              objectFit: 'cover',*/}
                {/*              borderRadius: '50%',*/}
                {/*              backgroundColor: theme.palette.grey[400]*/}
                {/*            }}*/}
                {/*          />*/}
                {/*          <Typography fontWeight={600}>*/}
                {/*            {item?.senderDetails?.fullName} / {item?.receiverDetails?.fullName}*/}
                {/*          </Typography>*/}
                {/*        </Box>*/}
                {/*      ))*/}
                {/*    ) : (*/}
                {/*      <Typography>No conversations found.</Typography>*/}
                {/*    )}*/}
                {/*  </TabPanel>*/}
                {/*</TabContext>*/}
              </Box>
            </>
          )}
        </Grid>

        {/* Chat Messages */}
        {selectedChat ? (
          <Grid
            item
            lg={9}
            md={9}
            sm={8}
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              // Recessed canvas against the raised list rail (background.paper), so the
              // two panes read as separate surfaces instead of one merged window.
              bgcolor: 'background.default'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: 1,
                py: 1,
                px: 2,
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                color: '#fff',
                bgcolor: theme.palette.background.paper
              }}
            >
              <Box display="flex" alignItems="center" gap={4}>
                {selectedChat?.participants?.map((user, index) => (
                  <Box display="flex" gap={1} justifyContent="center" alignItems="center" key={index}>
                    <img
                      src={user?.profileImage || '/default-avatar.png'}
                      alt={user?.fullName}
                      width="40"
                      height="40"
                      style={{
                        objectFit: 'cover',
                        borderRadius: '50%',
                        backgroundColor: theme.palette.grey[400]
                      }}
                    />
                    <Box>
                      <Typography fontWeight={600}>{user?.fullName}</Typography>
                      <Typography>{user?.userName}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <OutlinedInput
                sx={{ width: '30%' }}
                id="input-search-message"
                value={searchMessage}
                onChange={(e) => setSearchMessage(e.target.value)}
                placeholder="Search Message.."
                endAdornment={
                  <InputAdornment position="end">
                    <Typography>{`(${searchMatches.length > 0 ? currentMatchIndex + 1 : 0}/${searchMatches.length})`}</Typography>
                    <IconButton onClick={() => handleMatchNavigation('up')}>
                      <IconArrowUp stroke={1} />
                    </IconButton>
                    <IconButton onClick={() => handleMatchNavigation('down')}>
                      <IconArrowDown stroke={1} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>

            <Box
              ref={chatContainerRef}
              sx={{
                mt: 2,
                flexGrow: 1,
                overflowY: 'auto'
              }}
              onScroll={handleChatScroll}
            >
              {userChatLoader ? (
                <Box mx={'auto'}>
                  <CircularProgress />
                </Box>
              ) : chat.length > 0 ? (
                chat?.map((item, index) => {
                  const isSearchMatch = searchMatches?.includes(index);
                  const parts = item?.attachment ? null : item?.content?.split(new RegExp(`(${searchMessage})`, 'gi'));
                  const isPreviousMessageDifferentDay = index > 0 && isDifferentDay(item.timestamp, chat[index - 1].timestamp);

                  return (
                    <React.Fragment key={index}>
                      {isPreviousMessageDifferentDay && (
                        <Box
                          sx={{
                            textAlign: 'center',
                            width: '100%',
                            my: 2,
                            position: 'relative'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.5,
                              borderRadius: '10px',
                              backgroundColor: 'rgba(103, 58, 183, 0.2)',
                              color: '#fff'
                            }}
                          >
                            {formatDateForSeparator(item.timestamp)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        id={`chat-${index}`}
                        md={9}
                        sx={{
                          marginBottom: 2,
                          padding: 2,
                          color: 'text.primary',
                          // Both branches of this ternary were the same purple, so while
                          // stepping through search results you couldn't tell the match
                          // you were ON from the others. The current hit now reads solid;
                          // the rest sit back on the tint.
                          backgroundColor: isSearchMatch
                            ? currentMatchIndex === searchMatches?.indexOf(index)
                              ? 'accent.main'
                              : 'accent.soft'
                            : 'transparent',
                          borderRadius: '12px'
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <img
                            src={item?.senderDetail?.profileImage || '/default-avatar.png'}
                            alt={item?.senderDetail?.fullName}
                            width="40"
                            height="40"
                            style={{
                              objectFit: 'cover',
                              borderRadius: '50%',
                              backgroundColor: theme.palette.grey[400]
                            }}
                          />
                          <Box>
                            {/* The reply button is a SIBLING of the name, not nested inside
                                it: Typography renders a <p>, and a button inside a paragraph
                                is invalid markup that browsers relocate. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography fontWeight={600}>
                                {item?.senderDetail?.fullName}
                                <Typography
                                  component="span"
                                  sx={{
                                    ml: 1,
                                    color: theme.palette.grey[400],
                                    fontSize: '0.85rem',
                                    fontWeight: 400
                                  }}
                                >
                                  {formatMessageTime(item?.timestamp)}
                                </Typography>
                              </Typography>
                              <Tooltip title="Reply to this message">
                                <IconButton
                                  size="small"
                                  onClick={() => setReplyTarget(item)}
                                  sx={{ color: 'primary.light', p: 0.3 }}
                                >
                                  <ReplyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {/* The message this one replies to. Resolved server-side on
                                read, so it never shows stale text. */}
                            {item?.replyTo && (
                              <Box
                                sx={{
                                  borderLeft: '3px solid',
                                  borderColor: 'primary.main',
                                  bgcolor: 'rgba(0,0,0,0.22)',
                                  borderRadius: '0 6px 6px 0',
                                  px: 1.2,
                                  py: 0.6,
                                  mb: 0.8,
                                  maxWidth: 420
                                }}
                              >
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.light' }}>
                                  {item?.replyTo?.sender?.fullName || 'Message'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 12.5,
                                    opacity: 0.8,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {item?.replyTo?.content || (item?.replyTo?.attachment ? 'Attachment' : '')}
                                </Typography>
                              </Box>
                            )}
                            {item?.attachment ? (
                              <Box>
                                {item?.attachment?.fileType?.startsWith('audio/') ? (
                                  // Voice message / audio — inline player
                                  <audio
                                    controls
                                    preload="none"
                                    src={item?.attachment?.url}
                                    style={{ marginTop: '10px', maxWidth: '260px' }}
                                  />
                                ) : item?.attachment?.url && item?.attachment?.url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                                  // Image attachment
                                  <img
                                    src={item?.attachment?.url}
                                    alt="user-image"
                                    width="100"
                                    height="100"
                                    style={{
                                      objectFit: 'cover',
                                      marginTop: '10px',
                                      cursor: 'pointer' // Add pointer cursor style
                                    }}
                                    onClick={() => handleOpenModal(item?.attachment?.url)}
                                  />
                                ) : item?.attachment?.url && item?.attachment?.url.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i) ? (
                                  // Video attachment
                                  <video
                                    src={item?.attachment?.url}
                                    width="200"
                                    height="150"
                                    controls
                                    style={{
                                      marginTop: '10px',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleOpenModal(item?.attachment?.url)}
                                  />
                                ) : (
                                  // Non-image/video attachment
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      marginTop: '10px',
                                      padding: '8px',
                                      borderRadius: '4px',
                                      backgroundColor: 'rgba(255,255,255,0.1)',
                                      cursor: 'pointer',
                                      maxWidth: '200px'
                                    }}
                                    onClick={() => {
                                      // For non-image/video files, we open in a new tab or download
                                      window.open(item?.attachment?.url, '_blank');
                                    }}
                                  >
                                    {getFileIcon(item?.attachment?.url, true)}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#fff',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {item?.attachment?.originalname || 'File'}
                                    </Typography>
                                  </Box>
                                )}
                                <Typography
                                  variant="body2"
                                  dir="auto"
                                  sx={{
                                    mt: 0.5,
                                    color: '#fff',
                                    fontFamily: TEXT_WITH_EMOJI_FONT_STACK
                                  }}
                                >
                                  {item.content}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" dir="auto" sx={{ mt: 0.5, color: '#fff', fontFamily: TEXT_WITH_EMOJI_FONT_STACK }}>
                                {searchMessage && parts && !/[\u0600-\u06FF]/.test(item.content) ? (
                                  parts.map((part, i) => (
                                    <span
                                      key={i}
                                      style={{
                                        color: '#fff',
                                        backgroundColor:
                                          part?.toLowerCase() === searchMessage?.toLowerCase() ? 'black' : 'transparent'
                                      }}
                                    >
                                      {part}
                                    </span>
                                  ))
                                ) : (
                                  item.content
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                })
              ) : (
                <Typography padding={3} sx={{ color: '#fff' }}>
                  No messages found.
                </Typography>
              )}
              {(userChatLoader || isLoadingMoreMessages) && (
                <Box mx={'auto'} my={1}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>

            {/* Compose box — reply as CalisFix (client sees "CalisFix Coach") */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: theme.palette.background.paper
              }}
            >
              {isRecording ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', color: '#fff' }}>
                  <IconButton color="error" onClick={cancelRecording} title="Cancel">
                    <CloseIcon />
                  </IconButton>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 5,
                      bgcolor: 'rgba(244, 67, 54, 0.12)'
                    }}
                  >
                    <FiberManualRecordIcon
                      sx={{
                        color: 'error.main',
                        fontSize: 14,
                        animation: 'cf-rec-pulse 1.2s ease-in-out infinite',
                        '@keyframes cf-rec-pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.25 }
                        }
                      }}
                    />
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatRecordTime(recordSeconds)} / 1:00
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>Recording…</Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton color="primary" onClick={stopRecordingAndSend} title="Send voice message">
                    {isSending ? <CircularProgress size={20} /> : <IconSend stroke={1.5} />}
                  </IconButton>
                </Box>
              ) : (
                <>
                  {replyTarget && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                        mb: 1,
                        px: 1.2,
                        py: 0.8,
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        borderRadius: '0 6px 6px 0'
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.light' }}>
                          Replying to {replyTarget?.senderDetail?.fullName || 'message'}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12.5, opacity: 0.8 }}>
                          {replyTarget?.content || (replyTarget?.attachment ? 'Attachment' : '')}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setReplyTarget(null)} sx={{ color: '#fff' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <IconButton sx={{ color: '#fff' }} onClick={(e) => setEmojiAnchor(e.currentTarget)} title="Emoji">
                    <EmojiEmotionsIcon />
                  </IconButton>
                  <IconButton sx={{ color: '#fff' }} onClick={startRecording} disabled={isSending} title="Record voice message">
                    <MicIcon />
                  </IconButton>
                  <OutlinedInput
                    fullWidth
                    multiline
                    maxRows={4}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message…  (sent as CalisFix Coach)"
                    // Emoji rendered smaller than the text because the app font stack has
                    // no emoji glyphs and the browser picked its own fallback. Naming the
                    // colour emoji fonts as the tail of the stack sizes them with the text.
                    sx={{
                      color: '#fff',
                      fontFamily: TEXT_WITH_EMOJI_FONT_STACK,
                      // 14px left both the typed text and its emoji looking undersized
                      // next to the 24px icon buttons beside them.
                      fontSize: '1rem',
                      '& textarea, & input': { fontSize: '1rem', lineHeight: 1.5 }
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton color="primary" disabled={isSending || !messageText.trim()} onClick={handleSendMessage}>
                          {isSending ? <CircularProgress size={20} /> : <IconSend stroke={1.5} />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </>
              )}
            </Box>

            <Popover
              open={Boolean(emojiAnchor)}
              anchorEl={emojiAnchor}
              onClose={() => setEmojiAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {/* The emoji are TEXT, so they inherit IconButton's colour (MUI's
                  action.active, a 54%-opacity grey) and the app font stack
                  ('Noto Sans Arabic', 'Roboto') which carries no emoji glyphs — between
                  them the picker rendered washed-out/grey. Paint them with an explicit
                  colour-emoji stack at full opacity and stop them inheriting the grey. */}
              <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.25, maxWidth: 320 }}>
                {EMOJIS.map((emoji) => (
                  <IconButton
                    key={emoji}
                    size="small"
                    onClick={() => insertEmoji(emoji)}
                    sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1,
                      color: 'initial',
                      opacity: 1,
                      fontFamily: EMOJI_FONT_STACK
                    }}
                  >
                    {emoji}
                  </IconButton>
                ))}
              </Box>
            </Popover>
          </Grid>
        ) : (
          <Typography padding={3} sx={{ color: '#fff' }}>
            Select a chat to view messages.
          </Typography>
        )}
      </Grid>

      {/* Modal to display the attachment */}
      {openModal && (
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="lg"
          sx={{
            '& .MuiDialog-paper': {
              minHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
              maxHeight: { xs: '90vh', sm: '85vh', md: '80vh' },
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          fullWidth
          aria-labelledby="alert-dialog-title"
          disableEscapeKeyDown
          disableBackdropClick={true}
        >
          <Box>
            {attachmentUrl && attachmentUrl.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)$/i) ? (
              // Video preview
              <video src={attachmentUrl} controls style={{ width: '100%', height: 'auto', maxHeight: '70vh' }} autoPlay />
            ) : (
              // Image preview
              <img src={attachmentUrl} alt="Attachment" style={{ width: '100%', height: 'auto' }} />
            )}
          </Box>
        </Dialog>
      )}
    </>
  );
}

export default AdminChat;
